import { z } from "zod";

const id = z.uuid();
const actor = z.object({ type: z.enum(["user", "agent", "service", "system", "integration"]), id: z.string().min(1).max(200).nullable() }).strict();

export const TOOL_FACTORY_PROPOSAL_STATES = ["proposed", "implementing", "validating", "awaiting_approval", "active", "deprecated", "revoked", "failed"] as const;
export type ToolFactoryProposalState = (typeof TOOL_FACTORY_PROPOSAL_STATES)[number];

export const toolSandboxPolicySchema = z.object({
  filesystem: z.enum(["none", "workspace_read", "workspace_write"]),
  // D41-B8: only what the sandbox enforces (apps/sandbox/src/protocol.ts NetworkMode), never more: no network by
  // default, or the registry proxy for dependency installs. Arbitrary egress is not offered to generated tools.
  network: z.enum(["none", "registry"]).default("none"),
  process: z.boolean(),
  max_runtime_ms: z.number().int().positive().max(30 * 60 * 1000),
  max_output_bytes: z.number().int().positive().max(10_000_000),
}).strict();
export type ToolSandboxPolicy = z.infer<typeof toolSandboxPolicySchema>;

export const toolFactoryProposalSchema = z.object({
  id,
  organization_id: id,
  project_id: id,
  tool_key: z.string().regex(/^[a-z][a-z0-9_.-]{1,99}$/),
  purpose: z.string().min(1).max(2000),
  state: z.enum(TOOL_FACTORY_PROPOSAL_STATES),
  created_by: actor,
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
}).strict();

export const toolFactoryVersionSchema = z.object({
  id,
  proposal_id: id,
  version: z.number().int().positive(),
  implementation_ref: z.string().min(1).max(1000),
  implementation_sha256: z.string().regex(/^[a-f0-9]{64}$/),
  input_schema: z.record(z.string(), z.unknown()),
  sandbox_policy: toolSandboxPolicySchema,
  validation: z.object({ passed: z.boolean(), evidence_refs: z.array(z.string().max(500)).max(100), summary: z.string().max(4000) }).strict(),
  created_by: actor,
  created_at: z.iso.datetime(),
}).strict();

export const toolFactoryApprovalSchema = z.object({
  id,
  proposal_id: id,
  version_id: id,
  decision: z.enum(["approve", "reject"]),
  reviewer: actor,
  reason: z.string().min(1).max(4000),
  created_at: z.iso.datetime(),
}).strict();

export const toolFactoryActivationSchema = z.object({
  id,
  proposal_id: id,
  version_id: id,
  state: z.enum(["pending", "active", "failed", "deactivated"]),
  registry_ref: z.string().max(1000).nullable(),
  receipt: z.record(z.string(), z.unknown()),
  created_at: z.iso.datetime(),
  settled_at: z.iso.datetime().nullable(),
}).strict();

export const toolFactoryActivationRequestSchema = z.object({
  proposal_id: id,
  version_id: id,
  expected_proposal_state: z.enum(["awaiting_approval", "active", "deprecated"]),
  idempotency_key: z.string().min(8).max(200),
}).strict();

export type ToolFactoryProposal = z.infer<typeof toolFactoryProposalSchema>;
export type ToolFactoryVersion = z.infer<typeof toolFactoryVersionSchema>;
export type ToolFactoryApproval = z.infer<typeof toolFactoryApprovalSchema>;
export type ToolFactoryActivation = z.infer<typeof toolFactoryActivationSchema>;
